import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router";
import { suggestCompanyCode, type CompanyCodeCheck, type SessionInfo } from "@techplanner/shared/core";
import { useAuth } from "../auth";
import { Alert, Button, Field } from "../components/ui";
import { api, ApiError, errorMessage } from "../lib/api";
import { buildAppIcons, normalizeCompanyCode } from "../lib/company";

const CODE_MESSAGES: Record<NonNullable<CompanyCodeCheck["reason"]>, string> = {
  taken: "Ce code est déjà pris.",
  reserved: "Ce code est réservé.",
  invalid: "Lettres minuscules, chiffres et tirets uniquement."
};

/** Identifiant proposé à partir du prénom : « Paul Dupont » → « paul ». */
function suggestUsername(displayName: string): string {
  return suggestCompanyCode(displayName.split(" ")[0] ?? "").replace(/-/g, "").slice(0, 40) || "admin";
}

/** Inscription : le patron crée le compte de sa société et devient son administrateur. */
export function SignupPage() {
  const { user, signIn } = useAuth();
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState("");
  const [code, setCode] = useState("");
  const [codeEdited, setCodeEdited] = useState(false);
  const [codeCheck, setCodeCheck] = useState<CompanyCodeCheck | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [usernameEdited, setUsernameEdited] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [website, setWebsite] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Le code suit le nom de la société tant qu'on ne l'a pas modifié à la main.
  useEffect(() => {
    if (!codeEdited) setCode(suggestCompanyCode(companyName));
  }, [companyName, codeEdited]);
  useEffect(() => {
    if (!usernameEdited) setUsername(displayName.trim() ? suggestUsername(displayName) : "");
  }, [displayName, usernameEdited]);

  // Vérification de disponibilité, un instant après la dernière frappe.
  useEffect(() => {
    setCodeCheck(null);
    if (!code) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      api<CompanyCodeCheck>(`/public/company-codes/${encodeURIComponent(code)}`, { signal: controller.signal })
        .then(setCodeCheck)
        .catch(() => undefined);
    }, 350);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [code]);

  if (user) return <Navigate to="/" replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (loading) return;
    setError(null);
    setErrors({});
    setLoading(true);
    try {
      const session = await api<SessionInfo>("/public/signup", {
        method: "POST",
        body: { companyName, companyCode: code, displayName, email, username, password, acceptTerms, website: website || undefined }
      });
      localStorage.setItem("tp:last-username", session.user.username);
      signIn(session);
      // Icône de l’app installée dès le départ (initiale sur la couleur), avant même la personnalisation.
      void buildAppIcons(session.company)
        .then((body) => api("/company/icons", { method: "POST", body }))
        .catch(() => undefined);
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setErrors(err.fields);
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  const codeError = errors.companyCode ?? (codeCheck && !codeCheck.available && codeCheck.reason ? CODE_MESSAGES[codeCheck.reason] : undefined);
  const canSubmit = companyName.trim() && code && displayName.trim() && email.trim() && username && password.length >= 10 && acceptTerms;

  return (
    <main className="login signup">
      <img className="login-logo" src="/icons/icon.svg" alt="" width={56} height={56} />
      <h1>Créer le compte de votre société</h1>
      <p className="text-2" style={{ marginTop: 6, marginBottom: 24 }}>
        En quelques minutes, votre équipe a son application de planning et d'interventions, à vos couleurs.
      </p>
      <form className="form" onSubmit={submit} noValidate>
        {error && <Alert kind="error">{error}</Alert>}

        <Field label="Nom de la société" htmlFor="su-company" error={errors.companyName}>
          <input
            id="su-company"
            className="input"
            autoComplete="organization"
            value={companyName}
            maxLength={80}
            onChange={(e) => setCompanyName(e.target.value)}
          />
        </Field>
        <Field
          label="Code société"
          htmlFor="su-code"
          error={codeError}
          hint={
            codeCheck?.available
              ? `Disponible. Votre équipe se connectera avec le lien ${window.location.host}/s/${code}`
              : "Il figure dans le lien d'invitation de votre équipe. Il ne pourra plus changer."
          }
        >
          <input
            id="su-code"
            className="input"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={code}
            maxLength={40}
            onChange={(e) => {
              setCodeEdited(true);
              setCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""));
            }}
            onBlur={() => setCode((c) => normalizeCompanyCode(c.replace(/^-+|-+$/g, "")) ?? c)}
          />
        </Field>

        <Field label="Votre nom" htmlFor="su-name" error={errors.displayName}>
          <input id="su-name" className="input" autoComplete="name" value={displayName} maxLength={80} onChange={(e) => setDisplayName(e.target.value)} />
        </Field>
        <Field label="Votre e-mail" htmlFor="su-email" error={errors.email} hint="Pour récupérer votre mot de passe et recevoir vos factures.">
          <input
            id="su-email"
            className="input"
            type="email"
            autoComplete="email"
            autoCapitalize="none"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Votre identifiant" htmlFor="su-username" error={errors.username} hint="Pour vous connecter, avec le code société.">
          <input
            id="su-username"
            className="input"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={username}
            maxLength={40}
            onChange={(e) => {
              setUsernameEdited(true);
              setUsername(e.target.value.toLowerCase());
            }}
          />
        </Field>
        <Field label="Mot de passe" htmlFor="su-password" error={errors.password} hint="10 caractères minimum.">
          <div style={{ position: "relative" }}>
            <input
              id="su-password"
              className="input"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ paddingRight: 96 }}
            />
            <button type="button" className="btn ghost small" style={{ position: "absolute", right: 4, top: 6 }} onClick={() => setShowPassword((v) => !v)}>
              {showPassword ? "Masquer" : "Afficher"}
            </button>
          </div>
        </Field>

        {/* Champ piège : invisible pour les personnes, rempli par les robots. */}
        <div className="trap" aria-hidden="true">
          <label htmlFor="su-website">Site web</label>
          <input id="su-website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
        </div>

        <label className="checkbox-row">
          <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} />
          <span className="small">
            J'accepte les conditions d'utilisation de TechPlanner et la conservation de mes données pour faire fonctionner le service.
          </span>
        </label>
        {errors.acceptTerms && <Alert kind="error">{errors.acceptTerms}</Alert>}

        <Button type="submit" variant="primary" size="large" block loading={loading} disabled={!canSubmit || codeCheck?.available === false}>
          Créer mon compte
        </Button>
        <p className="small muted" style={{ textAlign: "center" }}>
          Votre compte sera validé par TechPlanner sous 24 h ouvrées. Vous pouvez tout préparer en attendant.
        </p>
        <p className="small" style={{ textAlign: "center" }}>
          <Link to="/login">J'ai déjà un compte</Link>
        </p>
      </form>
    </main>
  );
}
